// ============================================================================
// Overall Control — Orders tab API client (real data). Same conventions as
// ../../control_tower/control_tower.api.ts: the app's default axios instance carries baseURL + JWT.
// ============================================================================
import axios from "axios";
import {
  OcBranchOption,
  OcOrderPath,
  OcOrdersList,
  OcOrdersOverview,
  OcPosOverview,
  OcScopeParams,
  OcSearchResult,
  OcShipmentsOverview,
} from "../types/orders";
import { SlaSettings, SlaValue } from "../types/sla";

const BASE = "/overall-control";

const params = (s: OcScopeParams) => ({
  date_from: s.date_from,
  date_to: s.date_to,
  cities: s.cities?.join(",") || undefined,
  delivery_types: s.delivery_types?.join(",") || undefined,
  carriers: s.carriers?.join(",") || undefined,
  branches: s.branches?.join(",") || undefined,
});

// The EC filter's choices: every branch, then "No EC".
export const getBranches = async (): Promise<OcBranchOption[]> => (await axios.get(`${BASE}/branches/`)).data;

export const getOrdersOverview = async (s: OcScopeParams): Promise<OcOrdersOverview> =>
  (await axios.get(`${BASE}/orders/overview/`, { params: params(s) })).data;

export const getOrdersList = async (s: OcScopeParams, metric: string): Promise<OcOrdersList> =>
  (await axios.get(`${BASE}/orders/list/`, { params: { ...params(s), metric } })).data;

export const searchOverallControl = async (q: string): Promise<OcSearchResult> =>
  (await axios.get(`${BASE}/search/`, { params: { q } })).data;

// `allocationIds`: the PO(s) of the row it was opened from (a shipment: every PO on it), highlighted.
export const getOrderPath = async (salesOrderId: number, allocationIds?: number[]): Promise<OcOrderPath> =>
  (await axios.get(`${BASE}/orders/path/`, {
    params: { sales_order_id: salesOrderId, allocation_id: allocationIds?.length ? allocationIds.join(",") : undefined },
  })).data;

// ---- POs tab ----------------------------------------------------------------------------------

export const getPosOverview = async (s: OcScopeParams): Promise<OcPosOverview> =>
  (await axios.get(`${BASE}/pos/overview/`, { params: { ...params(s), carriers: undefined } })).data;

export const getPosList = async (s: OcScopeParams, metric: string): Promise<OcOrdersList> =>
  (await axios.get(`${BASE}/pos/list/`, { params: { ...params(s), carriers: undefined, metric } })).data;

// ---- Shipments tab ----------------------------------------------------------------------------

export const getShipmentsOverview = async (s: OcScopeParams): Promise<OcShipmentsOverview> =>
  (await axios.get(`${BASE}/shipments/overview/`, { params: params(s) })).data;

export const getShipmentsList = async (s: OcScopeParams, metric: string): Promise<OcOrdersList> =>
  (await axios.get(`${BASE}/shipments/list/`, { params: { ...params(s), metric } })).data;

// ---- SLA settings (admins only; a 403 means the tab is hidden) ---------------------------------

export const getSlaSettings = async (): Promise<SlaSettings> => (await axios.get(`${BASE}/sla/`)).data;

export const saveSlaSettings = async (values: Record<string, SlaValue>, note: string): Promise<SlaSettings> =>
  (await axios.put(`${BASE}/sla/`, { values, note })).data;
